import { BulkActionsComponent } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useTranslation } from 'core/i18n/i18n';
import { settingsModulePaths } from 'core/routes/routes';
import { usePopupManager } from 'react-popup-manager';
import { useNavigate } from 'react-router-dom';
import { DeleteMaterialConfirmationComponent } from '../settings-material-definitions-list/components/delete-material-confirmation';
import { useMaterialDefinitionDeleter } from '../settings-material-definitions-list/hooks/use-material-definition-deleter.hook';
import { useMaterialDefinitionsListSelection } from 'modules/settings/contexts/material-definitions/use-material-definitions-list-selection.hook';
import {
    BulkDeleteMaterialComponent,
    BulkDeleteMaterialsConfirmationComponent,
    BulkEditMaterialComponent,
} from './components';
import { useMaterialDefinitionsBulkDeleter } from './hooks/use-material-definitions-bulk-deleter.hook';

export const MaterialDefinitionsBulkActionsContainer: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const popupManager = usePopupManager();
    const { selectedRowIds, selectedMaterials, clearSelection } = useMaterialDefinitionsListSelection();
    const { deleteMaterial } = useMaterialDefinitionDeleter();
    const { bulkDeleteMaterials } = useMaterialDefinitionsBulkDeleter();

    const selectedCount = selectedRowIds.size;

    if (selectedCount === 0) {
        return null;
    }

    const handleEdit = () => {
        const [material] = selectedMaterials;
        if (material) {
            navigate(settingsModulePaths.materialDefinitionsForm.replace(':materialDefinitionId', material.id));
        }
    };

    const handleDelete = () => {
        // A single selection reuses the named single-delete confirmation (and its singular
        // notification), so the wording matches deleting one material from its row actions.
        if (selectedCount === 1 && selectedMaterials.length === 1) {
            const [material] = selectedMaterials;
            popupManager.open(DeleteMaterialConfirmationComponent, {
                materialName: material.name,
                onConfirm: () => deleteMaterial(material.id).then(clearSelection),
            });
            return;
        }

        popupManager.open(BulkDeleteMaterialsConfirmationComponent, {
            count: selectedCount,
            onConfirm: () => bulkDeleteMaterials([...selectedRowIds]),
        });
    };

    return (
        <BulkActionsComponent
            options={[
                <PocTypography key="bulk_actions_selected" element="span" variant="subtitle1">
                    {t('settings.materials.material-definitions.bulk-actions.selected', { amount: selectedCount })}
                </PocTypography>,
                <PocButton key="bulk_actions_clear" variant="tertiary" size="medium" onClick={clearSelection}>
                    {t('settings.materials.material-definitions.bulk-actions.clear-all')}
                </PocButton>,
            ]}
        >
            {selectedCount === 1 && <BulkEditMaterialComponent onClick={handleEdit} />}
            <BulkDeleteMaterialComponent onClick={handleDelete} />
        </BulkActionsComponent>
    );
};
